import { Component, ErrorInfo, ReactNode } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import { lighten } from '@mui/material/styles'
import Typography from '@mui/material/Typography'

interface Props {
  children?: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error: error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo)
    this.setState({ errorInfo: errorInfo })
  }

  public render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <Stack
            justifyContent="center"
            alignItems="center"
            height="100%"
            mb={10}
          >
            <Card
              sx={{
                p: 5,
                maxWidth: '75vw',
                border: 1,
                borderColor: 'error.main',
                backgroundColor: lighten('#d32f2f', 0.95)
              }}
              elevation={1}
            >
              <CardContent>
                <Typography variant="h2" color="error">
                  Whoops...something went wrong
                </Typography>
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <Typography
                    style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}
                  >
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>
        )
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
